import React, { useEffect, useMemo, useState } from 'react';
import {
    ActivitiesPageQuery,
    ActivityEntityName,
    ActivityTypeName,
    RoleId,
    useActivitiesPageLazyQuery,
    useCurrentUserQuery,
} from '../../../services';
import {
    CTableWrapper,
    DateRange,
    DAutocomplete,
    DBlock,
    DDateRangePicker,
    DIdAutocomplete,
    DPage,
    DPageBody,
    DPaginator,
    EditIcon,
    selectPaginated,
    usePaginatedListBlock,
} from '../../dum';
import { Box, IconButton, Typography } from '@mui/material';
import {
    COLUMN_LABELS,
    DATE_FORMAT,
    FIELD_LABELS,
    formatBusinessDateWithRelative,
    getActivityEntityLabel,
    getActivityTypeLabel,
    isSnapshotCaptureFailed,
    shortLabel,
    SNAPSHOT_CAPTURE_FAILED_HINT,
    useNumber,
    useString,
} from '../../../helpers';
import {
    ActivityChangesContent,
    SUpsertAccountDialog,
    SUpsertEmployeeDialog,
    SUpsertExpenseDialog,
    SUpsertMachineDialog,
    SUpsertOrderAdjustmentDialog,
    SUpsertOrderProductionDialog,
    SUpsertOrderRequestDialog,
    SUpsertOrderSale,
    SUpsertProductDialog,
    SUpsertResourceDialog,
    SUpsertTransferDialog,
    SUpsertUserDialog,
} from '../../smart';
import dayjs from 'dayjs';
import { WarningAmber } from '@mui/icons-material';

type ActivityDoc =
    ActivitiesPageQuery['paginatedActivities']['docs'][number];

interface DialogState {
    open: boolean;
    id: null | number;
    entityName: ActivityEntityName | null;
}

// Which entities the edit button can actually open. Previously a `switch` with
// eight cases inside the click handler, while the button itself was enabled for
// everything — so an activity on an expense, a transfer or a resource rendered a
// button that did nothing at all when clicked. Driving both the button and the
// dialog off one set makes that impossible: no entry here, no button.
//
// Absent on purpose: account products and expense resources (no form to open),
// and production plans (a page, not a dialog).
const EDITABLE_ENTITIES: ActivityEntityName[] = [
    ActivityEntityName.OrderSale,
    ActivityEntityName.OrderRequest,
    ActivityEntityName.OrderProduction,
    ActivityEntityName.OrderAdjustment,
    ActivityEntityName.Product,
    ActivityEntityName.Account,
    ActivityEntityName.Employee,
    ActivityEntityName.Machine,
    ActivityEntityName.Expense,
    ActivityEntityName.Transfer,
    ActivityEntityName.Resource,
    ActivityEntityName.User,
];

// Filter options. Built from the enums rather than hand-listed so a new entity
// appears in the dropdown as soon as it exists; the ones with no Spanish label
// are the dead enum values, and they are dropped rather than shown blank.
interface FilterOption {
    value: string;
    label: string;
}

const ENTITY_OPTIONS: FilterOption[] = Object.values(ActivityEntityName)
    .map((entityName) => ({
        value: entityName as string,
        label: getActivityEntityLabel(entityName),
    }))
    .filter((option) => !!option.label);

const TYPE_OPTIONS: FilterOption[] = Object.values(ActivityTypeName).map(
    (type) => ({
        value: type as string,
        label: getActivityTypeLabel(type),
    }),
);

export function ActivitiesPage() {
    const KEY = ActivitiesPage.name;

    const [query, { data, previousData, loading }] =
        useActivitiesPageLazyQuery();

    const [dialog, setDialog] = useState<DialogState>({
        entityName: null,
        open: false,
        id: null,
    });

    // Column filters beyond the shared date range. Persisted like every other
    // list block's extra filters, so returning to the page keeps them.
    const [entityName, setEntityName] = useString({ KEY: KEY + '_EntityName' });
    const [type, setType] = useString({ KEY: KEY + '_Type' });
    const [userId, setUserId] = useNumber({ KEY: KEY + '_UserId' });

    const {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        skip,
        take,
        setSkip,
    } = usePaginatedListBlock({
        storageKey: KEY,
        resetDeps: [entityName, type, userId],
    });

    // The audit snapshots hold whole rows, so getActivity is gated on the server
    // to the three global roles — Super, General and Asistente General (the last
    // is globally read-only and reads the audit, never writes it). Hide the
    // expander for everyone else rather than let them click into an error.
    const { data: currentUserData } = useCurrentUserQuery();
    const canReadAudit =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin, RoleId.Guest].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );
    // Asistente General is a reader of the audit only. Keep the edit action
    // restricted to the two global write-capable roles even though Guest can
    // expand rows and read their snapshots.
    const canEditAudit =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    useEffect(() => {
        void query({
            variables: {
                skip,
                take,
                startDate: startDate || null,
                endDate: endDate || null,
                entityName: (entityName as ActivityEntityName) || null,
                type: (type as ActivityTypeName) || null,
                userId,
            },
        });
    }, [query, skip, take, startDate, endDate, entityName, type, userId]);

    const { docs: activities, count } = selectPaginated(
        data?.paginatedActivities,
        previousData?.paginatedActivities,
    );

    const resetDialog = useMemo(() => {
        return () => {
            setDialog({
                entityName: null,
                open: false,
                id: null,
            });
        };
    }, [setDialog]);

    const isDialogOpen = (entity: ActivityEntityName) =>
        dialog.entityName === entity && dialog.open;

    return (
        <DPage>
            <DPageBody>
                <DBlock>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            gap: 2,
                        }}
                    >
                        <Typography
                            variant={'subtitle1'}
                            fontWeight={'bold'}
                            sx={{ flex: 1 }}
                        >
                            Actividades
                        </Typography>
                        <DPaginator
                            skip={skip}
                            setSkip={setSkip}
                            take={take}
                            count={count}
                            disableGutters
                        />
                    </Box>

                    <CTableWrapper
                        loading={loading}
                        // Only audit readers get the expander; undefined removes
                        // the whole column for everyone else.
                        collapse={
                            canReadAudit
                                ? (activity: ActivityDoc) => (
                                      <ActivityChangesContent
                                          activityId={activity.id}
                                          showHeader={false}
                                      />
                                  )
                                : undefined
                        }
                        items={[
                            {
                                cellGrow: 5,
                                title: shortLabel(FIELD_LABELS.created_at),
                                textAlign: 'center',
                                filter: (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            '& .MuiTextField-root': {
                                                minWidth: 0,
                                            },
                                        }}
                                    >
                                        <DDateRangePicker
                                            label={'Fecha'}
                                            value={
                                                startDate && endDate
                                                    ? [
                                                          dayjs(startDate),
                                                          dayjs(endDate),
                                                      ]
                                                    : undefined
                                            }
                                            onChange={(range: DateRange) => {
                                                setStartDate(
                                                    range[0].format(
                                                        DATE_FORMAT,
                                                    ),
                                                );
                                                setEndDate(
                                                    range[1].format(
                                                        DATE_FORMAT,
                                                    ),
                                                );
                                            }}
                                            onClear={() => {
                                                setStartDate('');
                                                setEndDate('');
                                            }}
                                            disableGutters
                                        />
                                    </Box>
                                ),
                                // Absolute date AND relative: "hace 3 meses"
                                // alone is unusable in an audit trail. Read on
                                // the Mexico City business day, so the column
                                // agrees with what the date filter above it
                                // selects — created_at is a true timestamp, and
                                // an evening activity here is already tomorrow
                                // in UTC.
                                content: (activity) =>
                                    formatBusinessDateWithRelative(
                                        activity.created_at,
                                    ),
                            },
                            {
                                cellGrow: 4,
                                title: shortLabel(COLUMN_LABELS.activity),
                                filter: (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            '& .MuiTextField-root': {
                                                minWidth: 0,
                                            },
                                        }}
                                    >
                                        <DAutocomplete
                                            label={'Actividad'}
                                            itemText={'label'}
                                            itemValue={'value'}
                                            value={
                                                ENTITY_OPTIONS.find(
                                                    (option) =>
                                                        option.value ===
                                                        entityName,
                                                ) ?? null
                                            }
                                            options={ENTITY_OPTIONS}
                                            onChange={(option) => {
                                                setEntityName(
                                                    option?.value ?? '',
                                                );
                                            }}
                                            minWidth={0}
                                            disableGutters
                                        />
                                    </Box>
                                ),
                                content: (activity) =>
                                    getActivityEntityLabel(
                                        activity.entity_name,
                                    ),
                            },
                            {
                                cellGrow: 8,
                                title: shortLabel(COLUMN_LABELS.activity_title),
                                // The warning rides on the title rather than
                                // taking its own column: it is rare, and a
                                // permanently empty column costs every row.
                                // Visible without expanding, which is the point
                                // — an unexpanded row otherwise looks normal.
                                content: (activity) =>
                                    isSnapshotCaptureFailed(
                                        activity.snapshot_status,
                                    ) ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <WarningAmber
                                                fontSize={'small'}
                                                color={'warning'}
                                                titleAccess={
                                                    SNAPSHOT_CAPTURE_FAILED_HINT
                                                }
                                            />
                                            {activity.title}
                                        </Box>
                                    ) : (
                                        activity.title
                                    ),
                            },
                            {
                                cellGrow: 4,
                                title: shortLabel(COLUMN_LABELS.activity_type),
                                filter: (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            '& .MuiTextField-root': {
                                                minWidth: 0,
                                            },
                                        }}
                                    >
                                        <DAutocomplete
                                            label={'Tipo'}
                                            itemText={'label'}
                                            itemValue={'value'}
                                            value={
                                                TYPE_OPTIONS.find(
                                                    (option) =>
                                                        option.value === type,
                                                ) ?? null
                                            }
                                            options={TYPE_OPTIONS}
                                            onChange={(option) => {
                                                setType(option?.value ?? '');
                                            }}
                                            minWidth={0}
                                            disableGutters
                                        />
                                    </Box>
                                ),
                                content: (activity) =>
                                    getActivityTypeLabel(activity.type),
                            },
                            {
                                cellGrow: 5,
                                title: shortLabel(FIELD_LABELS.user_id),
                                filter: (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            '& .MuiTextField-root': {
                                                minWidth: 0,
                                            },
                                        }}
                                    >
                                        <DIdAutocomplete
                                            id={userId}
                                            onChange={setUserId}
                                            itemText={'fullname'}
                                            label={'Usuario'}
                                            options={data?.users || []}
                                            disableGutters
                                        />
                                    </Box>
                                ),
                                content: (activity) =>
                                    activity.user?.fullname || '—',
                            },
                            {
                                cellGrow: 2,
                                title: '',
                                textAlign: 'center',
                                content: (activity) => {
                                    // A deleted record has nothing to open, and
                                    // an entity with no form has nowhere to go —
                                    // render no button rather than a dead one.
                                    const entity =
                                        activity.entity_name as ActivityEntityName;
                                    if (!canEditAudit) {
                                        return null;
                                    }
                                    if (
                                        activity.type ===
                                        ActivityTypeName.Delete
                                    ) {
                                        return null;
                                    }
                                    if (
                                        EDITABLE_ENTITIES.indexOf(entity) === -1
                                    ) {
                                        return null;
                                    }

                                    return (
                                        <IconButton
                                            size="small"
                                            title={'Editar'}
                                            sx={{
                                                bgcolor:
                                                    'rgba(34,197,94,0.15)',
                                                boxShadow: 2,
                                                borderRadius: '50%',
                                            }}
                                            onClick={() => {
                                                setDialog({
                                                    id: activity.entity_id,
                                                    entityName: entity,
                                                    open: true,
                                                });
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    );
                                },
                            },
                        ]}
                        data={activities}
                    />
                </DBlock>
            </DPageBody>

            <SUpsertOrderSale
                open={isDialogOpen(ActivityEntityName.OrderSale)}
                setOpen={resetDialog}
                orderSaleId={dialog.id}
            />
            <SUpsertOrderRequestDialog
                open={isDialogOpen(ActivityEntityName.OrderRequest)}
                setOpen={resetDialog}
                orderRequestId={dialog.id}
            />
            <SUpsertOrderProductionDialog
                open={isDialogOpen(ActivityEntityName.OrderProduction)}
                setOpen={resetDialog}
                orderProductionId={dialog.id}
            />
            <SUpsertOrderAdjustmentDialog
                open={isDialogOpen(ActivityEntityName.OrderAdjustment)}
                setOpen={resetDialog}
                orderAdjustmentId={dialog.id}
            />
            <SUpsertProductDialog
                open={isDialogOpen(ActivityEntityName.Product)}
                setOpen={resetDialog}
                productId={dialog.id}
            />
            <SUpsertAccountDialog
                open={isDialogOpen(ActivityEntityName.Account)}
                setOpen={resetDialog}
                accountId={dialog.id}
            />
            <SUpsertEmployeeDialog
                open={isDialogOpen(ActivityEntityName.Employee)}
                setOpen={resetDialog}
                employeeId={dialog.id}
            />
            <SUpsertMachineDialog
                open={isDialogOpen(ActivityEntityName.Machine)}
                setOpen={resetDialog}
                machineId={dialog.id}
            />
            <SUpsertExpenseDialog
                open={isDialogOpen(ActivityEntityName.Expense)}
                setOpen={resetDialog}
                expenseId={dialog.id}
            />
            <SUpsertTransferDialog
                open={isDialogOpen(ActivityEntityName.Transfer)}
                setOpen={resetDialog}
                transferId={dialog.id}
            />
            <SUpsertResourceDialog
                open={isDialogOpen(ActivityEntityName.Resource)}
                setOpen={resetDialog}
                resourceId={dialog.id}
            />
            <SUpsertUserDialog
                open={isDialogOpen(ActivityEntityName.User)}
                setOpen={resetDialog}
                userId={dialog.id}
            />
        </DPage>
    );
}
